import {LabelTypeMetadata} from "../NutritionLabels/LabelTypeMetadata";
import {NutrientType} from "../Ingredient/NutrientType";
import {NutrientAnalysis} from "@/models/Nutrient/NutrientAnalysis";
import {AdditionalLabelInformation} from "@/models/NutritionLabels/AdditionalLabelInformation";
import {round} from "@/utils/Helpers";
import {RecipeServingType} from "@/models/Recipes/RecipeServingType";
import {CanadaFOPHighInMetadata} from "@/models/NutritionLabels/CanadaFOPHighInMetadata";

export class Nutritions {
  unadjustedAddedSugarPerServing: number = 0;

  totalNutrientsPerServing: (NutrientAnalysis & { nutrientName: string })[] = [];
  servingUnitOfMeasurement: string = 'g';

  weight: number = 0;
  weightPerServing: number = 0;
  roundedWeightPerServing: number = 0;
  rawWeight: number = 0;
  rawWeightPerServing: number = 0;

  volume: number | null;
  rawVolume: number | null;
  volumePerServing: number | null;
  rawVolumePerServing: number | null;

  quantityPerServingInUnitOfMeasurement: {
    item1: number; // quantity
    item2: string; // unit of measurement
  } | null;

  displayServingQuantityAndMeasure: {
    item1: number; // quantity
    item2: string; // unit of measurement
  } | null;
  servingQuantityInUnitOfMeasurementOverride: number;
  servingUnitOfMeasurementOverride: string;
  labelType : LabelTypeMetadata | LabelTypeMetadata.NutritionFacts;

  additionalLabelInformation: AdditionalLabelInformation;
  moistureYieldFactor: number;
  ingredientScaleMultiplier: number;
  servings: number;
  originalMoistureInRecipe: number;

  constructor(
    totalNutrientsPerServing: (NutrientAnalysis & { nutrientName: string })[],
    servingUnitOfMeasurement: string,
    weight: number,
    weightPerServing: number,
    roundedWeightPerServing: number,
    rawWeight: number,
    rawWeightPerServing: number,
    volume: number | null,
    rawVolume: number | null,
    volumePerServing: number | null,
    rawVolumePerServing: number | null,
    quantityPerServingInUnitOfMeasurement: {
      item1: number; // quantity
      item2: string; // unit of measurement
    } = { item1: 0, item2: 'g' },
    displayServingQuantityAndMeasure: {
      item1: number;
      item2: string;
    } = { item1: 0, item2: 'g' },
    servingQuantityInUnitOfMeasurementOverride: number = null,
    servingUnitOfMeasurementOverride: string = null,
    labelType: LabelTypeMetadata | LabelTypeMetadata.NutritionFacts = LabelTypeMetadata.NutritionFacts,
    additionalLabelInformation: AdditionalLabelInformation = {},
    moistureYieldFactor: number = 1,
    ingredientScaleMultiplier: number = 1,
    servings: number = 1,
    originalMoistureInRecipe: number = 0,
  ) {
    this.totalNutrientsPerServing = totalNutrientsPerServing;
    this.servingUnitOfMeasurement = servingUnitOfMeasurement;
    this.weight = weight;
    this.weightPerServing = weightPerServing;
    this.roundedWeightPerServing = roundedWeightPerServing;
    this.rawWeight = rawWeight;
    this.rawWeightPerServing = rawWeightPerServing;
    this.volume = volume;
    this.rawVolume = rawVolume;
    this.volumePerServing = volumePerServing;
    this.rawVolumePerServing = rawVolumePerServing;
    this.quantityPerServingInUnitOfMeasurement = quantityPerServingInUnitOfMeasurement;
    this.displayServingQuantityAndMeasure = displayServingQuantityAndMeasure;
    this.servingQuantityInUnitOfMeasurementOverride = servingQuantityInUnitOfMeasurementOverride;
    this.servingUnitOfMeasurementOverride = servingUnitOfMeasurementOverride;
    this.labelType = labelType;
    this.additionalLabelInformation = additionalLabelInformation;
    this.moistureYieldFactor = moistureYieldFactor;
    this.ingredientScaleMultiplier = ingredientScaleMultiplier;
    this.servings = servings;
    this.originalMoistureInRecipe = originalMoistureInRecipe;
  }

  getNutrientInfo(...nutrientNames: NutrientType[]): NutrientAnalysis | null {
    const nameKey = nutrientNames.map((x) => NutrientType[x]).join('');

    // Find by name
    return this.totalNutrientsPerServing.find(
      (nutrient) => nutrient.nutrientName === nameKey
    ) || null;
  }

  getRoundedValue(...nutrientNames: NutrientType[]): number {
    const nutrientInfo = this.getNutrientInfo(...nutrientNames);
    return nutrientInfo ? nutrientInfo.roundedValue : 0;
  }

  getRoundedDailyValue(...nutrientNames: NutrientType[]): number {
    const nutrientInfo = this.getNutrientInfo(...nutrientNames);
    return nutrientInfo ? nutrientInfo.roundedDailyValue : 0;
  }

  getExactValue(...nutrientNames: NutrientType[]): number {
    const nutrientInfo = this.getNutrientInfo(...nutrientNames);
    return nutrientInfo ? nutrientInfo.exactValue : 0;
  }

  getExactDailyValue(...nutrientNames: NutrientType[]): number {
    const nutrientInfo = this.getNutrientInfo(...nutrientNames);
    return nutrientInfo ? nutrientInfo.exactDailyValue : 0;
  }

  factorMoistureLossToRawQuantity(rawQuantity: number, invert: boolean = true) {
    let moistureYieldFactor = this.moistureYieldFactor;

    if (invert) {
      moistureYieldFactor = 1 - moistureYieldFactor;
    }

    return round(rawQuantity * moistureYieldFactor, 2);
  }

  getRawRecipeWeight(recipeServingType: RecipeServingType) {
    const invert = recipeServingType === RecipeServingType.Servings || this.moistureYieldFactor === 0;

    if (
      this.servingUnitOfMeasurement == 'ml'
      || this.servingUnitOfMeasurement == 'fl. oz'
    ) {
      return this.factorMoistureLossToRawQuantity(this.rawVolume, invert);
    }

    return this.factorMoistureLossToRawQuantity(this.rawWeight, invert);
  }

  containsHighIn(highInNutrient: CanadaFOPHighInMetadata) {
    if (
      !(2 in this.additionalLabelInformation)
      || this.additionalLabelInformation[LabelTypeMetadata.FrontOfPack].highInMetadata === null) {
      return false;
    }

    return (this.additionalLabelInformation[LabelTypeMetadata.FrontOfPack].highInMetadata & highInNutrient) > 0;
  }
}